/**
 * GenerationScreen — full-content-area view while OpenAI designs the
 * templates and the deterministic compiler turns the Design JSON into
 * editable editor documents. Shows real pipeline stages, supports cancel,
 * and surfaces errors with retry. No simulation, no fabricated output.
 */
import React from 'react';
import { Button } from '../../../components/ui/button';
import { Loader2, Check, Square, AlertTriangle, RotateCcw } from 'lucide-react';
import { useGenerationRun } from '../../hooks/useGenerationRun';

const GenerationScreen = ({ selections, brand, settings, count, provider, runToken, onComplete, onCancel, onRetry }) => {
  const { stages, stageIndex, stage, percent, error, cancel } = useGenerationRun({
    selections, brand, settings, count, provider, runToken, onComplete,
  });

  if (error) {
    return (
      <div className="min-h-[420px] bg-white rounded-2xl border border-gray-200 p-10 flex flex-col items-center justify-center text-center" data-testid="generation-error">
        <AlertTriangle className="w-10 h-10 text-amber-500 mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Generation failed</h2>
        <p className="text-sm text-gray-500 max-w-md mb-6">{error.message}</p>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={onCancel}>Back</Button>
          <Button className="bg-purple-600 hover:bg-purple-700" onClick={onRetry}>
            <RotateCcw className="w-4 h-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[420px] bg-white rounded-2xl border border-gray-200 p-10 flex flex-col items-center justify-center" data-testid="generation-screen">
      <div className="w-full max-w-md text-center">
        <Loader2 className="w-10 h-10 text-purple-600 animate-spin mx-auto mb-4" />
        <h2 className="text-lg font-semibold text-gray-900 mb-1">{stage.label}</h2>
        <p className="text-xs text-gray-400 mb-6">
          Designing {count} unique concept{count > 1 ? 's' : ''} · {percent}%
        </p>

        <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-8">
          <div className="h-full bg-purple-600 rounded-full transition-all duration-300" style={{ width: `${percent}%` }} />
        </div>

        <ol className="text-left space-y-1.5 mb-8">
          {stages.map((s, i) => (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              {i < stageIndex ? (
                <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" />
              ) : i === stageIndex ? (
                <Loader2 className="w-3.5 h-3.5 text-purple-600 animate-spin flex-shrink-0" />
              ) : (
                <span className="w-3.5 h-3.5 rounded-full border border-gray-200 flex-shrink-0" />
              )}
              <span className={i <= stageIndex ? 'text-gray-800' : 'text-gray-300'}>{s.label}</span>
            </li>
          ))}
        </ol>

        <Button variant="outline" onClick={() => { cancel(); onCancel(); }}>
          <Square className="w-4 h-4 mr-2" /> Cancel
        </Button>
        <p className="text-[11px] text-gray-300 mt-6">OpenAI is the designer — the compiler stays deterministic.</p>
      </div>
    </div>
  );
};

export default GenerationScreen;
